import { useMemo } from 'react';
import { useGetKitAssetVersionsQuery, useGetReleaseKitQuery } from '../../../../redux/api/fymAPI';
import { AssetViewerItem } from '../viewer/viewerItems';
import { kitRevisionEntries, reelRevisionEntries, RevisionEntry } from './revisionModel';

export interface RevisionEntriesState {
  entries: RevisionEntry[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The revision history of the asset that the viewer shows (design d1). A kit asset reads
 * `GET .../assets/:uuid/versions`; a reel reads the reels of its song from the release kit answer
 * (the cache that the Assets tab already holds). The realtime events of the kit invalidate both.
 */
export function useRevisionEntries(input: {
  accountId: number;
  catalogItemUuid: string;
  item: AssetViewerItem;
}): RevisionEntriesState {
  const { accountId, catalogItemUuid, item } = input;
  const isKit = item.kind === 'kitAsset';
  const versions = useGetKitAssetVersionsQuery(
    { accountId, catalogItemUuid, assetUuid: item.assetUuid },
    { skip: !isKit }
  );
  const release = useGetReleaseKitQuery({ accountId, catalogItemUuid }, { skip: isKit });

  const entries = useMemo(() => {
    if (isKit) {
      return kitRevisionEntries(versions.data?.versions ?? []);
    }
    return item.reel === null ? [] : reelRevisionEntries(release.data?.reels ?? [], item.reel);
  }, [isKit, item.reel, release.data, versions.data]);

  return {
    entries,
    isLoading: isKit ? versions.isLoading : release.isLoading,
    isError: isKit ? versions.isError : release.isError,
  };
}
